import React from 'react';
import { Box } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { TIPO_SECAO_HOME_LABEL, TipoSecaoHome } from '../../../../model/apm/apm-enums';
import { IHomeSectionResponse } from '../../../../model/apm/home/response/home-section-response';
import { IHomeSectionItemResponse } from '../../../../model/apm/home/response/home-section-item-response';
import { SortableList } from '../../components/sortable-list';
import { SectionCard } from '../../components/section-card';
import { ItemRow } from '../../components/item-row';
import { StatusChips } from '../../components/status-chips';
import { AcessoRapidoIcon } from '../../components/apm-icons';
import { EmptyState } from '../../../../components/empty-state';
import { HOME_SECTION_CONFIG } from './home-section-config';
import { BannerMockup, NewsMockup } from './home-section-mockup';
import { HOME_SECTION_ICONS } from './home-section-icons';
import { formatDate } from '../../../../utils/date';

interface HomeSectionListProps {
    sections: IHomeSectionResponse[];
    readOnly: boolean;
    onEditSection: (section: IHomeSectionResponse) => void;
    onDeleteSection: (section: IHomeSectionResponse) => void;
    onToggleSectionVisibility: (section: IHomeSectionResponse) => void;
    onReorderSections: (next: IHomeSectionResponse[]) => void;
    onAddItem: (section: IHomeSectionResponse) => void;
    onEditItem: (section: IHomeSectionResponse, item: IHomeSectionItemResponse) => void;
    onDeleteItem: (section: IHomeSectionResponse, item: IHomeSectionItemResponse) => void;
    onToggleItemVisibility: (section: IHomeSectionResponse, item: IHomeSectionItemResponse) => void;
    onReorderItems: (section: IHomeSectionResponse, next: IHomeSectionItemResponse[]) => void;
}

function sectionSummary(section: IHomeSectionResponse): string | undefined {
    if (section.type === TipoSecaoHome.Noticias) return `Exibe as ${section.itemsQuantity ?? 0} notícias mais recentes`;
    if (section.type === TipoSecaoHome.DiarioOficial) return 'Últimas edições publicadas';
    return section.subtitle || section.content?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function sectionMockup(section: IHomeSectionResponse): React.ReactNode {
    if (section.type === TipoSecaoHome.Banners) return <BannerMockup section={section} />;
    if (section.type === TipoSecaoHome.Noticias) return <NewsMockup section={section} />;
    return undefined;
}

function itemSubtitle(item: IHomeSectionItemResponse): string | undefined {
    const period = item.startDate || item.endDate
        ? `Exibição: ${formatDate(item.startDate) || '...'} a ${formatDate(item.endDate) || '...'}`
        : undefined;
    return [item.description, item.url, period].filter(Boolean).join(' · ') || undefined;
}

function itemLeading(type: TipoSecaoHome, item: IHomeSectionItemResponse): React.ReactNode {
    if (type === TipoSecaoHome.AcessoRapido) return <AcessoRapidoIcon name={item.icon} />;
    if (type === TipoSecaoHome.Videos) return <PlayCircleIcon color="error" fontSize="small" />;
    if (item.image) return <ImageIcon color="primary" fontSize="small" />;
    return undefined;
}

export function HomeSectionList(props: HomeSectionListProps) {
    const { sections, readOnly } = props;

    function renderSection(section: IHomeSectionResponse, dragHandle: React.ReactNode) {
        const config = HOME_SECTION_CONFIG[section.type];
        const items = section.items || [];
        return (
            <SectionCard
                dragHandle={dragHandle}
                icon={HOME_SECTION_ICONS[section.type]}
                title={section.title || TIPO_SECAO_HOME_LABEL[section.type]}
                subtitle={sectionSummary(section)}
                chips={<StatusChips typeLabel={TIPO_SECAO_HOME_LABEL[section.type]} isFixed={section.isFixed} isVisible={section.isVisible} />}
                isVisible={section.isVisible}
                canDelete={!readOnly && !section.isFixed}
                deleteDisabledReason={section.isFixed ? 'Seções fixas não podem ser excluídas; apenas ocultadas' : undefined}
                itemCount={items.length}
                itemNoun={config?.itemNoun}
                actionsDisabled={readOnly}
                onToggleVisibility={() => props.onToggleSectionVisibility(section)}
                onEdit={() => props.onEditSection(section)}
                onDelete={() => props.onDeleteSection(section)}
                onAddItem={config?.itemFields && !readOnly ? () => props.onAddItem(section) : undefined}
                preview={sectionMockup(section)}
            >
                {items.length === 0 ? (
                    <EmptyState
                        icon={HOME_SECTION_ICONS[section.type]}
                        message={`Nenhum(a) ${(config?.itemNoun || 'item').toLowerCase()} cadastrado(a) nesta seção`}
                    />
                ) : (
                    <SortableList
                        items={items}
                        getId={(i) => i.id}
                        onReorder={(next) => props.onReorderItems(section, next)}
                        renderItem={(item, itemHandle) => (
                            <ItemRow
                                dragHandle={itemHandle}
                                leading={itemLeading(section.type, item)}
                                title={item.title || `${config?.itemNoun || 'Item'} sem título`}
                                subtitle={itemSubtitle(item)}
                                chips={<StatusChips isVisible={item.isVisible} />}
                                dimmed={!item.isVisible}
                                isVisible={item.isVisible}
                                onToggleVisibility={() => props.onToggleItemVisibility(section, item)}
                                onEdit={() => props.onEditItem(section, item)}
                                onDelete={() => props.onDeleteItem(section, item)}
                            />
                        )}
                    />
                )}
            </SectionCard>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <SortableList
                items={sections}
                getId={(s) => s.id}
                onReorder={props.onReorderSections}
                disabled={readOnly}
                renderItem={renderSection}
            />
        </Box>
    );
}
